import { axialToWorld } from '../math/HexMath';
import type { WorldConfig } from '../config/WorldConfig';

/** Per-instance data: offsetX, offsetZ, r, g, b */
const FLOATS_PER_INSTANCE = 5;

/**
 * A single cluster of hexes (default 50×50 = 2500 hexes).
 * Hex positions use GLOBAL grid coordinates so clusters
 * tile seamlessly regardless of world size.
 */
export class Cluster {
  readonly col: number;
  readonly row: number;
  readonly key: string;
  readonly instanceData: Float32Array;
  readonly hexCount: number;

  readonly boundsMinX: number;
  readonly boundsMinZ: number;
  readonly boundsMaxX: number;
  readonly boundsMaxZ: number;

  constructor(col: number, row: number, config: WorldConfig) {
    this.col = col;
    this.row = row;
    this.key = `${col},${row}`;

    const w = config.clusterWidth;
    const h = config.clusterHeight;
    const hr = config.hexRadius;
    this.hexCount = w * h;

    const globalColStart = col * w;
    const globalRowStart = row * h;

    const data = new Float32Array(this.hexCount * FLOATS_PER_INSTANCE);

    let minX = Infinity, minZ = Infinity;
    let maxX = -Infinity, maxZ = -Infinity;

    for (let lr = 0; lr < h; lr++) {
      for (let lc = 0; lc < w; lc++) {
        const gr = globalRowStart + lr;
        const gc = globalColStart + lc;

        const offset = (gr + (gr & 1)) >> 1;
        const q = gc - offset;
        const r = gr;

        const [wx, wz] = axialToWorld(q, r, hr);

        if (wx < minX) minX = wx;
        if (wx > maxX) maxX = wx;
        if (wz < minZ) minZ = wz;
        if (wz > maxZ) maxZ = wz;

        const i = lr * w + lc;
        const base = i * FLOATS_PER_INSTANCE;
        data[base] = wx;
        data[base + 1] = wz;

        data[base + 2] = 1.0;
        data[base + 3] = 1.0;
        data[base + 4] = 1.0;
      }
    }

    this.boundsMinX = minX - hr;
    this.boundsMinZ = minZ - hr;
    this.boundsMaxX = maxX + hr;
    this.boundsMaxZ = maxZ + hr;
    this.instanceData = data;
  }
}
